import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../context/ThemeContext';
import { MinoLogo } from './chamaTheme';

const FONT_SANS = "'Geist', 'SF Pro Display', -apple-system, sans-serif";
const FONT_MONO = "'Geist Mono', 'SF Mono', monospace";
const API = '/api/chamas';

const fmtKES = (n) => `KES ${parseFloat(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const initials = (name) => (name || '??').trim().slice(0, 2).toUpperCase();
const fmtROI = (roi) => `${roi >= 0 ? '+' : ''}${roi.toFixed(1)}%`;
const MEDAL_COLORS = { 1: '#F0A500', 2: '#C0C0C0', 3: '#CD7F32' };
const PERIODS = [{ label: 'All time', param: 'all' }, { label: 'This month', param: 'month' }, { label: 'This week', param: 'week' }];

function ChamaRow({ chama, rank, index, T }) {
  const [hovered, setHovered] = useState(false);
  const roi = chama.roi_pct !== null ? parseFloat(chama.roi_pct) : null;
  const roiColor = roi === null ? T.textDim : roi >= 0 ? T.yes : T.no;
  const rankColor = MEDAL_COLORS[rank] || T.textDim;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid', gridTemplateColumns: '32px 36px 1fr auto auto auto',
        alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 10,
        background: hovered ? T.surfaceHi : 'transparent',
        transition: 'background 0.15s',
        animation: `fadeSlide 0.35s ease ${index * 0.04}s both`,
      }}
    >
      <div style={{ color: rankColor, fontFamily: FONT_MONO, fontSize: 13, fontWeight: 700, textAlign: 'center' }}>
        {rank ? `#${rank}` : '—'}
      </div>
      <div style={{
        width: 32, height: 32, borderRadius: 8, flexShrink: 0,
        background: T.accentDim, border: `1px solid ${T.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, fontWeight: 700, color: T.accent,
      }}>
        {initials(chama.name)}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: T.text, fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {chama.name || 'Unnamed'}
        </div>
        <div style={{ color: T.textDim, fontSize: 11 }}>
          {chama.member_count || 0} member{chama.member_count !== 1 ? 's' : ''}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ color: T.textDim, fontSize: 10, marginBottom: 1 }}>pool</div>
        <div style={{ color: T.textMuted, fontFamily: FONT_MONO, fontSize: 12 }}>{fmtKES(chama.wallet_balance)}</div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ color: T.textDim, fontSize: 10, marginBottom: 1 }}>volume</div>
        <div style={{ color: T.textMuted, fontFamily: FONT_MONO, fontSize: 12 }}>{fmtKES(chama.total_staked)}</div>
      </div>
      <div style={{ textAlign: 'right', minWidth: 60 }}>
        <div style={{ color: T.textDim, fontSize: 10, marginBottom: 1 }}>ROI</div>
        <div style={{ color: roiColor, fontFamily: FONT_MONO, fontSize: 14, fontWeight: 700 }}>
          {roi !== null ? fmtROI(roi) : '—'}
        </div>
      </div>
    </div>
  );
}

export default function ChamaLeaderboard({ onBack }) {
  const { T } = useTheme();
  const [data, setData] = useState([]);
const [loading, setLoading] = useState(true);
const [error, setError] = useState(null);
const [period, setPeriod] = useState(0);
const [search, setSearch] = useState('');
const [retryCount, setRetryCount] = useState(0);

useEffect(() => {
  const token = localStorage.getItem('token');
  setLoading(true); setError(null);
  fetch(`${API}/leaderboard/global?period=${PERIODS[period].param}`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
    .then(d => setData(d.leaderboard || []))
    .catch(err => { console.error(err); setError('Failed to load leaderboard.'); })
    .finally(() => setLoading(false));
}, [period, retryCount]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? data.filter(c => (c.name || '').toLowerCase().includes(q)) : data;
  }, [data, search]);

  const ranked = filtered.filter(m => m.roi_pct !== null);
  const unranked = filtered.filter(m => m.roi_pct === null);
  const top3 = data.filter(m => m.roi_pct !== null).slice(0, 3);

  const card = { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12 };

  return (
    <div style={{ minHeight: '100vh', background: T.bg, fontFamily: FONT_SANS, color: T.text }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeSlide { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
      `}</style>

      <nav style={{
        position: 'sticky', top: 0, zIndex: 100, height: 56,
        display: 'flex', alignItems: 'center', padding: '0 32px',
        background: `${T.bg}ee`, backdropFilter: 'blur(16px)',
        borderBottom: `1px solid ${T.border}`,
      }}>
        <button onClick={() => typeof onBack === 'function' && onBack()} style={{
          fontSize: 13, color: T.textMuted, padding: '6px 14px', borderRadius: 8,
          border: `1px solid ${T.border}`, background: T.surface, cursor: 'pointer',
          fontFamily: FONT_SANS, display: 'flex', alignItems: 'center', gap: 6,
        }}>← Back</button>
      </nav>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '32px 24px 80px', animation: 'fadeIn 0.4s ease' }}>

        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <h1 style={{ color: T.text, fontSize: 24, fontWeight: 700, margin: '0 0 6px', letterSpacing: '-0.02em' }}>Leaderboard</h1>
          <p style={{ color: T.textMuted, fontSize: 14, margin: 0 }}>Chamas ranked by ROI</p>
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 3, background: T.surfaceHi, borderRadius: 8, padding: 3, border: `1px solid ${T.border}` }}>
            {PERIODS.map((p, i) => (
              <button key={p.param} onClick={() => setPeriod(i)} style={{
                fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: FONT_SANS,
                background: period === i ? T.surface : 'transparent',
                color: period === i ? T.text : T.textMuted,
                border: period === i ? `1px solid ${T.border}` : '1px solid transparent',
                transition: 'all 0.15s',
              }}>{p.label}</button>
            ))}
          </div>
          <div style={{ position: 'relative' }}>
            <svg style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
              width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input placeholder="Find a chama…" value={search} onChange={e => setSearch(e.target.value)} style={{
              background: T.surface, border: `1px solid ${T.border}`, borderRadius: 8,
              padding: '8px 12px 8px 34px', fontSize: 13, color: T.text,
              fontFamily: FONT_SANS, outline: 'none', width: 180,
            }} />
          </div>
        </div>

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', border: `2px solid ${T.border}`, borderTop: `2px solid ${T.accent}`, animation: 'spin 0.8s linear infinite' }} />
          </div>
        )}

        {!loading && error && (
          <div style={{ ...card, padding: 40, textAlign: 'center' }}>
            <p style={{ color: T.no, fontSize: 14, marginBottom: 12 }}>{error}</p>
            <button onClick={() => setRetryCount(c => c + 1)} style={{
              fontSize: 13, padding: '7px 16px', borderRadius: 8, cursor: 'pointer',
              border: `1px solid ${T.border}`, background: T.surfaceHi, color: T.textMuted, fontFamily: FONT_SANS,
            }}>Retry</button>
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Podium */}
            {!search && top3.length >= 3 && (
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 12, marginBottom: 32, padding: '0 16px' }}>
                {[top3[1], top3[0], top3[2]].map((c, podiumIdx) => {
                  const rank = [2, 1, 3][podiumIdx];
                  const heights = [110, 140, 90];
                  const medalColor = MEDAL_COLORS[rank];
                  return (
                    <div key={c.id} style={{ flex: podiumIdx === 1 ? 1.2 : 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                      <div style={{ position: 'relative' }}>
                        <div style={{
                          width: podiumIdx === 1 ? 52 : 40, height: podiumIdx === 1 ? 52 : 40,
                          borderRadius: 10, background: T.accentDim,
                          border: `2px solid ${medalColor}60`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: podiumIdx === 1 ? 15 : 12, fontWeight: 700, color: T.accent,
                        }}>{initials(c.name)}</div>
                        <div style={{
                          position: 'absolute', bottom: -2, right: -2,
                          width: 17, height: 17, borderRadius: '50%',
                          background: medalColor, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 9, fontWeight: 700, color: '#000',
                        }}>{rank}</div>
                      </div>
                      <div style={{ color: T.text, fontSize: 12, fontWeight: 600, textAlign: 'center' }}>{c.name || 'Unnamed'}</div>
                      <div style={{ color: parseFloat(c.roi_pct) >= 0 ? T.yes : T.no, fontSize: podiumIdx === 1 ? 17 : 13, fontWeight: 700, fontFamily: FONT_MONO }}>
                        {fmtROI(parseFloat(c.roi_pct))}
                      </div>
                      <div style={{
                        width: '100%', height: heights[podiumIdx],
                        background: T.surfaceHi, border: `1px solid ${T.border}`, borderBottom: 'none',
                        borderRadius: '10px 10px 0 0',
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 10,
                        position: 'relative', overflow: 'hidden',
                      }}>
                        <div style={{ position: 'absolute', top: 0, left: '15%', right: '15%', height: 1, background: `linear-gradient(90deg, transparent, ${medalColor}60, transparent)` }} />
                        <div style={{ color: `${medalColor}90`, fontSize: 11, fontWeight: 600, letterSpacing: '0.06em' }}>#{rank}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Ranked */}
            {ranked.length > 0 && (
              <div style={{ ...card, padding: '8px', marginBottom: 16 }}>
                <div style={{ padding: '10px 14px 6px', color: T.textDim, fontSize: 10, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  Full Rankings · {ranked.length}
                </div>
                {ranked.map((c, i) => <ChamaRow key={c.id} chama={c} rank={i + 1} index={i} T={T} />)}
              </div>
            )}

            {/* Unranked */}
            {unranked.length > 0 && (
              <div style={{ ...card, padding: '8px' }}>
                <div style={{ padding: '10px 14px 6px', color: T.textDim, fontSize: 10, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                  Unranked · No settled trades
                </div>
                {unranked.map((c, i) => <ChamaRow key={c.id} chama={c} rank={null} index={i} T={T} />)}
              </div>
            )}

            {ranked.length === 0 && unranked.length === 0 && (
              <div style={{ ...card, padding: 40, textAlign: 'center', color: T.textDim, fontSize: 14 }}>
                {search.trim() ? `No chama matching "${search.trim()}"` : 'No chamas yet for this period'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}